import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUserProfile,
  updateUserProfile,
  requestRoleUpgrade,
  updateUserAvatar,
  deactivateAccount,
  deleteAccount,
  changePassword,
} from "../../services/api/userService";
import { getAllSports, getUserSports } from "../../services/api/sportsService";
import { dedupeCitySuggestions, formatCityLabel } from "../../utils/citySuggestions";
import GameLayout from "../layout/GameLayout";
import FloatingButton from "../ui/FloatingButton";
import NotificationPreferencesModal from "./NotificationPreferencesModal";
import BlockedUsersList from "./BlockedUsersList";
import { usePaymentsSetup } from "../payments/PriceField";
import { SPORTS_AVATARS } from "../../utils/avatars";
import { passwordProblem } from "../../utils/passwordPolicy";
import "../../styles/features/profile.css";

// Sport-specific position suggestions
const SPORT_POSITIONS = {
  'Volleyball':     ['Outside Hitter', 'Middle Blocker', 'Opposite Hitter', 'Setter', 'Libero', 'Defensive Specialist'],
  'Football':       ['Goalkeeper', 'Centre-back', 'Full-back', 'Wing-back', 'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder', 'Winger', 'Striker', 'Centre-forward'],
  'Basketball':     ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Centre'],
  'Cricket':        ['Batsman', 'Bowler', 'All-rounder', 'Wicket-keeper', 'Opening Batsman', 'Middle-order Batsman'],
  'Tennis':         ['Baseline Player', 'Serve-and-Volley', 'All-court Player'],
  'Badminton':      ['Singles Player', 'Doubles Player', 'Mixed Doubles Player'],
  'Table Tennis':   ['Singles Player', 'Doubles Player'],
  'Swimming':       ['Freestyle', 'Backstroke', 'Breaststroke', 'Butterfly', 'Individual Medley'],
  'Rugby':          ['Prop', 'Hooker', 'Lock', 'Flanker', 'Number 8', 'Scrum-half', 'Fly-half', 'Centre', 'Wing', 'Full-back'],
  'Hockey':         ['Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Striker'],
  'Ice Hockey':     ['Goaltender', 'Defenceman', 'Left Wing', 'Right Wing', 'Centre'],
  'Baseball':       ['Pitcher', 'Catcher', 'First Baseman', 'Second Baseman', 'Third Baseman', 'Shortstop', 'Left Fielder', 'Centre Fielder', 'Right Fielder'],
  'Athletics':      ['Sprinter', 'Middle Distance Runner', 'Long Distance Runner', 'Hurdler', 'High Jumper', 'Long Jumper', 'Shot Putter', 'Javelin Thrower', 'Decathlete'],
  'Cycling':        ['Road Cyclist', 'Track Cyclist', 'Mountain Biker', 'BMX Rider'],
  'Gymnastics':     ['Artistic Gymnast', 'Rhythmic Gymnast', 'Trampolinist'],
  'Boxing':         ['Amateur Boxer', 'Professional Boxer'],
  'Wrestling':      ['Freestyle Wrestler', 'Greco-Roman Wrestler'],
  'Judo':           ['Judoka'],
  'Karate':         ['Kata Competitor', 'Kumite Competitor'],
  'Taekwondo':      ['Sparring Competitor', 'Patterns Competitor'],
  'Golf':           ['Amateur', 'Professional', 'Club Player'],
  'Squash':         ['Singles Player', 'Doubles Player'],
  'Water Polo':     ['Goalkeeper', 'Defender', 'Centre Forward', 'Driver', 'Wing'],
  'Archery':        ['Recurve', 'Compound', 'Barebow'],
  'Fencing':        ['Foil', 'Épée', 'Sabre'],
  'Figure Skating': ['Singles Skater', 'Pairs Skater', 'Ice Dancer'],
  'Skiing':         ['Downhill', 'Slalom', 'Giant Slalom', 'Cross-country', 'Ski Jumper'],
  'Diving':         ['Platform Diver', 'Springboard Diver'],
  'Bowling':        ['Tenpin Bowler', 'Lawn Bowler'],
  'Darts':          ['Singles Player', 'Doubles Player'],
};

const GENDER_OPTIONS = [
  { value: 'male',   label: '♂ Male' },
  { value: 'female', label: '♀ Female' },
  { value: 'non-binary', label: '⚧ Non-binary' },
  { value: 'prefer_not_to_say', label: '🔒 Prefer not to say' },
];

function calcAge(dob) {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
}

// Roles that can already host leagues and tournaments: no "Host a league" button for them
const HOST_ROLES = ["league_admin", "organization_admin", "org_admin", "platform_admin", "super_admin"];
// Hosting is free during beta; the request still goes to an admin (pricing plan, Oct 2026)
const HOST_PLAN_ID = "league_organizer";

function Profile({ token, onLogout }) {
  const navigate = useNavigate();
  // The Payments button shows only while payments are switched on
  const paymentsSetup = usePaymentsSetup();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    username: "",
    height: "",
    weight: "",
    favoriteSports: "",
    bio: "",
    gender: "",
    jersey_number: "",
    nickname: "",
    date_of_birth: "",
    home_city: "",
  });
  const [originalProfile, setOriginalProfile] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  // Selected sports as an array (derived from favoriteSports string)
  const [selectedSports, setSelectedSports] = useState([]);
  // All sports fetched from API
  const [availableSports, setAvailableSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(false);
  const [showAllSports, setShowAllSports] = useState(false);

  // ── City autocomplete state ────────────────────────────────────────────────
  const [citySearchInput, setCitySearchInput]       = useState('');
  const [citySuggestions, setCitySuggestions]       = useState([]);
  const [citySearchLoading, setCitySearchLoading]   = useState(false);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  // True only when the home location came from the browser's geolocation
  // (the "GPS" button) — a searched city is not "GPS verified" (BUG-41).
  const [cityFromGps, setCityFromGps] = useState(false);
  const cityDebounceRef = useRef(null);
  const cityAbortRef    = useRef(null);
  
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeStatus, setUpgradeStatus] = useState("");
  const [upgradeError, setUpgradeError] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const [customFilePreview, setCustomFilePreview] = useState(null);
  const [customFileUploading, setCustomFileUploading] = useState(false);
  const [customFileError, setCustomFileError] = useState("");
  const [compressingImage, setCompressingImage] = useState(false);

  // Account settings modal state
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  // Notification preferences modal state
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  // 'menu' | 'deactivate-confirm' | 'delete-confirm' | 'change-password'
  const [accountView, setAccountView] = useState("menu");
  const [accountPassword, setAccountPassword] = useState("");
  const [accountReason, setAccountReason] = useState("");
  const [accountActionLoading, setAccountActionLoading] = useState(false);
  const [accountActionMsg, setAccountActionMsg] = useState("");
  // change-password fields
  const [cpCurrentPwd, setCpCurrentPwd] = useState("");
  const [cpNewPwd, setCpNewPwd] = useState("");
  const [cpConfirmPwd, setCpConfirmPwd] = useState("");
  const [cpShowCurrent, setCpShowCurrent] = useState(false);
  const [cpShowNew, setCpShowNew] = useState(false);
  const [cpShowConfirm, setCpShowConfirm] = useState(false);

  const closeAccountSettings = () => {
    setShowAccountSettings(false);
    setAccountView("menu");
    setAccountPassword("");
    setAccountReason("");
    setAccountActionMsg("");
    setCpCurrentPwd("");
    setCpNewPwd("");
    setCpConfirmPwd("");
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);

        // Get user role data from localStorage (like Dashboard does)
        const storedUser = localStorage.getItem("user");
        let storedAvatarUrl = "";
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          storedAvatarUrl = parsedUser.avatarUrl || "";
        }

        const userData = await getUserProfile();
        const apiRole = userData.role_name || userData.role || "player";
        const profileData = {
          name: userData.name || "",
          email: userData.email || "",
          username: userData.username || "",
          height: userData.height || "",
          weight: userData.weight || "",
          favoriteSports: userData.favorite_sports || "",
          bio: userData.bio || "",
          gender: userData.gender || "",
          jersey_number: userData.jersey_number ?? "",
          nickname: userData.nickname || "",
          date_of_birth: userData.date_of_birth
            ? userData.date_of_birth.slice(0, 10)
            : "",
          home_city: userData.home_city || "",
          home_lat: userData.home_lat ?? null,
          home_lng: userData.home_lng ?? null,
          role: apiRole,
          role_name: apiRole,
          roleName: userData.role_display_name || "Player",
          avatarUrl: userData.avatar_url || storedAvatarUrl,
        };
        setProfile(profileData);
        setOriginalProfile(profileData);
        // Sync city search input with saved home_city so the field shows the saved value
        if (profileData.home_city) setCitySearchInput(profileData.home_city);
        // Parse stored sports string into array for the multi-select UI
        if (userData.favorite_sports) {
          setSelectedSports(userData.favorite_sports.split(',').map(s => s.trim()).filter(Boolean));
        } else {
          // No favourites saved yet (e.g. sports picked during onboarding):
          // fall back to the sports the user joined (BUG-40). Not awaited so
          // the profile renders without waiting for it.
          Promise.resolve()
            .then(() => getUserSports())
            .then((mine) => {
              const list = mine?.data?.sports || mine?.sports || mine?.data || [];
              const names = (Array.isArray(list) ? list : [])
                .map((s) => s.name || s.sport_name)
                .filter(Boolean);
              const unique = [...new Set(names)];
              if (unique.length > 0) {
                setSelectedSports((prev) => (prev.length > 0 ? prev : unique));
                const joined = unique.join(', ');
                setProfile((prev) => (prev.favoriteSports ? prev : { ...prev, favoriteSports: joined }));
                setOriginalProfile((prev) => (prev.favoriteSports ? prev : { ...prev, favoriteSports: joined }));
              }
            })
            .catch(() => { /* non-critical — leave Favourite Sports empty */ });
        }

        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          const nextUser = {
            ...parsedUser,
            role: profileData.role,
            roleName: profileData.roleName,
          };
          localStorage.setItem("user", JSON.stringify(nextUser));
        }
      } catch (err) {
        setMessage(
          "Failed to load profile: " +
            (err.response?.data?.error || err.message)
        );
      } finally {
        setLoading(false);
      }
    };

    if (token || localStorage.getItem("user")) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only run when token changes

  // Load all sports for the multi-select picker
  useEffect(() => {
    const fetchSports = async () => {
      try {
        setSportsLoading(true);
        const res = await getAllSports({ limit: 100 });
        // API returns { success, data: [...] } (array) or { data: { sports: [...] } } (paginated)
        const rawData = res?.data;
        const list = Array.isArray(rawData) ? rawData : (rawData?.sports || res?.sports || []);
        // Filter out test/dummy entries; keep real sports alphabetically
        const real = list
          .filter(s => s.name && !s.name.startsWith('Test') && !s.name.startsWith('New'))
          .sort((a, b) => a.name.localeCompare(b.name));
        setAvailableSports(real);
      } catch (err) {
        console.error('Failed to load sports:', err);
      } finally {
        setSportsLoading(false);
      }
    };
    fetchSports();
  }, []);

  // ── City Geoapify autocomplete ─────────────────────────────────────────────

  const fetchCitySuggestions = useCallback((q) => {
    if (cityAbortRef.current) cityAbortRef.current.abort();
    cityAbortRef.current = new AbortController();

    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
    if (!apiKey || q.trim().length < 3) {
      setCitySuggestions([]);
      setCitySearchLoading(false);
      return;
    }

    setCitySearchLoading(true);
    // Filter to city/district/county level results only
    const url =
      `https://api.geoapify.com/v1/geocode/autocomplete` +
      `?text=${encodeURIComponent(q)}&limit=6&type=city&apiKey=${apiKey}`;

    fetch(url, { signal: cityAbortRef.current.signal })
      .then((r) => r.json())
      .then((data) => {
        setCitySearchLoading(false);
        setCitySuggestions(dedupeCitySuggestions(data?.features));
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setCitySearchLoading(false);
      });
  }, []);

  const handleCityInput = (e) => {
    const q = e.target.value;
    setCitySearchInput(q);
    setShowCitySuggestions(true);
    setCityFromGps(false);
    // If user clears the box also clear the saved coords
    if (!q) {
      setProfile((prev) => ({ ...prev, home_city: '', home_lat: null, home_lng: null }));
      setCitySuggestions([]);
      return;
    }
    clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => fetchCitySuggestions(q), 400);
  };

  const handleCitySelect = (feature) => {
    const p = feature.properties;
    const label = formatCityLabel(p);
    setCityFromGps(false);
    setProfile((prev) => ({
      ...prev,
      home_city: label,
      home_lat: p.lat,
      home_lng: p.lon,
    }));
    setCitySearchInput(label);
    setCitySuggestions([]);
    setShowCitySuggestions(false);
  };

  const handleClearCity = () => {
    setProfile((prev) => ({ ...prev, home_city: '', home_lat: null, home_lng: null }));
    setCitySearchInput('');
    setCitySuggestions([]);
    setCityFromGps(false);
  };

  // ── Handle GPS for home city (reverse geocode to get label) ───────────────

  const handleCityGps = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCityFromGps(true);
        setProfile((prev) => ({
          ...prev,
          home_lat: latitude,
          home_lng: longitude,
          home_city: prev.home_city || 'Current Location',
        }));
        setCitySearchInput(profile.home_city || 'Current Location');

        // Attempt a reverse geocode to get a real city label
        const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
        if (!apiKey) return;
        fetch(
          `https://api.geoapify.com/v1/geocode/reverse?lat=${latitude}&lon=${longitude}&apiKey=${apiKey}`
        )
          .then((r) => r.json())
          .then((data) => {
            const p = data?.features?.[0]?.properties;
            if (p) {
              const label =
                [p.city || p.county || p.name, p.state, p.country]
                  .filter(Boolean)
                  .join(', ');
              setProfile((prev) => ({ ...prev, home_city: label }));
              setCitySearchInput(label);
            }
          })
          .catch(() => {});
      },
      () => {}
    );
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  // Toggle a sport in/out of the selected list
  const toggleSport = (sportName) => {
    setSelectedSports(prev =>
      prev.includes(sportName)
        ? prev.filter(s => s !== sportName)
        : [...prev, sportName]
    );
  };

  // Check username availability with debounce
  // (username is read-only in edit mode — kept for potential future use)

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const profileData = {
        ...profile,
        height: profile.height === "" ? null : parseInt(profile.height) || null,
        weight: profile.weight === "" ? null : parseInt(profile.weight) || null,
        // Join selected sports array back to comma-separated string
        favoriteSports: selectedSports.join(', ') || "",
        jersey_number: profile.jersey_number === "" ? null : (parseInt(profile.jersey_number) || null),
        nickname: profile.nickname || "",
        date_of_birth: profile.date_of_birth || null,
        age: calcAge(profile.date_of_birth),
        home_city: profile.home_city || null,
        home_lat: profile.home_lat ?? null,
        home_lng: profile.home_lng ?? null,
      };

      await updateUserProfile(profileData);
      setMessage("Profile updated successfully!");
      setIsEditing(false);
      setOriginalProfile({ ...profileData, favoriteSports: selectedSports.join(', ') });

      // Sync jersey_number and nickname to localStorage so Dashboard reflects changes immediately
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          localStorage.setItem("user", JSON.stringify({
            ...parsed,
            jersey_number: profileData.jersey_number,
            nickname: profileData.nickname,
          }));
        } catch (_) {}
      }

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage(
        "Failed to update profile: " +
          (err.response?.data?.error || err.message)
      );
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setProfile(originalProfile);
    // Reset selected sports from the saved string
    const saved = originalProfile.favoriteSports || "";
    setSelectedSports(saved ? saved.split(',').map(s => s.trim()).filter(Boolean) : []);
    setMessage("");
  };

  const handleUpgrade = async () => {
    try {
      setUpgradeLoading(true);
      setUpgradeError("");
      setUpgradeStatus("");

      const response = await requestRoleUpgrade(HOST_PLAN_ID, {
        testMode: true,
      });

      const payload = response?.data || response;
      const targetRole = payload?.targetRole;
      const autoApproved = payload?.autoApproved;
      const status = payload?.status || "pending";

      if (autoApproved && targetRole) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          const updatedUser = {
            ...parsedUser,
            role: targetRole,
            roleName: targetRole,
          };
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }

        setProfile(prev => ({
          ...prev,
          role: targetRole,
          roleName: targetRole,
          role_name: targetRole,
        }));
      }

      if (status === "approved") {
        setUpgradeStatus("You can host leagues and tournaments now.");
      } else if (payload?.alreadyRequested) {
        setUpgradeStatus("Your request is already with our team. You'll get an email and a notification once it's approved.");
      } else {
        setUpgradeStatus("Request sent. You'll get an email and a notification once it's approved, usually within 24 to 48 hours.");
      }
    } catch (err) {
      const apiMessage =
        err.response?.data?.message || err.response?.data?.error || err.message;
      setUpgradeError(apiMessage || "Could not send the request. Please try again.");
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleAvatarSelect = async (avatarUrl, avatarType = "generic") => {
    try {
      setAvatarLoading(true);
      setAvatarError("");

      await updateUserAvatar(avatarUrl, avatarType);

      setProfile(prev => ({
        ...prev,
        avatar_url: avatarUrl,
        avatarUrl: avatarUrl,
        avatar_type: avatarType,
      }));

      // Update localStorage to reflect changes on dashboard
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        const updatedUser = {
          ...parsedUser,
          avatarUrl: avatarUrl,
          avatar_url: avatarUrl,
          avatar_type: avatarType,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        
        // Dispatch custom event to notify Dashboard of avatar change
        window.dispatchEvent(new CustomEvent('avatarUpdated', { detail: { avatarUrl, avatar_type: avatarType } }));
      }

      setShowAvatarModal(false);
      setMessage("Avatar updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      const apiMessage =
        err.response?.data?.message || err.response?.data?.error || err.message;
      setAvatarError(apiMessage || "Failed to update avatar");
    } finally {
      setAvatarLoading(false);
    }
  };

  // Compress image using Canvas API
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set target dimensions (400x400px for avatars)
          const maxWidth = 400;
          const maxHeight = 400;
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression (quality 0.75)
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.75);
          resolve(compressedBase64);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = event.target?.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleCustomFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setCustomFileError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setCustomFileError('File size must be less than 5MB');
      return;
    }

    setCustomFileError('');
    setCompressingImage(true);

    try {
      // Compress image on client-side
      const compressedBase64 = await compressImage(file);
      setCustomFilePreview(compressedBase64);
    } catch (err) {
      setCustomFileError('Failed to process image. Please try another file.');
      console.error('Image compression error:', err);
    } finally {
      setCompressingImage(false);
    }
  };

  const handleUploadCustomAvatar = async () => {
    if (!customFilePreview) {
      setCustomFileError('Please select an image first');
      return;
    }

    try {
      setCustomFileUploading(true);
      setCustomFileError('');

      await updateUserAvatar(customFilePreview, 'custom');

      setProfile(prev => ({
        ...prev,
        avatar_url: customFilePreview,
        avatarUrl: customFilePreview,
        avatar_type: 'custom',
      }));

      // Update localStorage to reflect changes on dashboard
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        const updatedUser = {
          ...parsedUser,
          avatarUrl: customFilePreview,
          avatar_url: customFilePreview,
          avatar_type: 'custom',
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      // Notify Dashboard and other components about the avatar change
      window.dispatchEvent(new CustomEvent('avatarUpdated', {
        detail: { avatarUrl: customFilePreview, avatar_type: 'custom' }
      }));

      setCustomFilePreview(null);
      setShowAvatarModal(false);
      setMessage('Custom avatar uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      const apiMessage =
        err.response?.data?.message || err.response?.data?.error || err.message;
      setCustomFileError(apiMessage || 'Failed to upload avatar');
    } finally {
      setCustomFileUploading(false);
    }
  };

  if (loading) {
    return <div className="profile-loading">Loading profile...</div>;
  }

  return (
    <GameLayout
      token={token}
      onLogout={onLogout}
      profile={profile}
      showFloatingNav={true}
    >
      <div className="profile-gaming-layout">
        {/* Gaming Profile Header - Floating */}
        <div className="profile-header-gaming">
          <div className="profile-header-content-floating">
            {/* Left side: Avatar + Name/Subtitle */}
            <div className="profile-header-left">
              <div 
                className="profile-avatar-gaming" 
                onClick={() => setShowAvatarModal(true)}
                role="button"
                tabIndex="0"
                title="Click to change avatar"
              >
                <div className="character-3d-container-small">
                  <div className="character-shadow-small"></div>
                  <div className="character-3d-avatar-small">
                    <img
                      src={
                        profile.avatar_url ||
                        profile.avatarUrl ||
                        '/assets/profile_avatar/sports_avatar.png'
                      }
                      alt="Player Avatar"
                      className="character-3d-image-small"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                    <div
                      className="character-3d-placeholder-small"
                      style={{ display: "none" }}
                    >
                      <div className="avatar-3d-icon-small">
                        {profile.role_name === "super_admin" ||
                        profile.role === "super_admin" ||
                        profile.roleName === "super_admin"
                          ? "🏆"
                          : "⚽"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="profile-info-gaming-floating">
                <h2 className="profile-title-gaming-floating">
                  {profile.name || "Player Profile"}
                </h2>
                {/* Role badge */}
                <span className="profile-role-badge">
                  {profile.roleName || profile.role_name || profile.role || 'Player'}
                </span>
                <p className="profile-subtitle-gaming-floating">
                  {profile.favoriteSports || "Multi-Sport"}
                </p>
              </div>
            </div>

            {/* Right side: Edit & Upgrade buttons */}
            {!isEditing && (
              <div className="profile-actions-gaming-floating">
                <FloatingButton
                  variant="primary"
                  size="medium"
                  onClick={() => setIsEditing(true)}
                  icon="✏️"
                  aria-label="Edit profile"
                  title="Edit profile"
                >
                  <span className="btn-text">Edit</span>
                </FloatingButton>
                {/* Host a league: for players who can't host yet */}
                {!HOST_ROLES.includes(profile.role_name || profile.role) && (
                  <FloatingButton
                    variant="warning"
                    size="medium"
                    onClick={() => { setUpgradeStatus(""); setUpgradeError(""); setShowUpgradeModal(true); }}
                    icon="🏟️"
                    aria-label="Host a league (Beta)"
                    title="Host a league: free while Sportifide is in beta"
                  >
                    <span className="btn-text">Host a league</span>
                    <span className="beta-badge" aria-hidden="true">Beta</span>
                  </FloatingButton>
                )}
                <FloatingButton
                  variant="secondary"
                  size="medium"
                  onClick={() => setShowNotificationSettings(true)}
                  icon="🔔"
                  title="Notification Settings"
                  aria-label="Notifications"
                >
                  <span className="btn-text">Notifications</span>
                </FloatingButton>
                {/* Payments: connect Stripe, payments made and received, refunds (only while payments are on) */}
                {paymentsSetup.enabled && (
                  <FloatingButton
                    variant="secondary"
                    size="medium"
                    onClick={() => navigate("/payments")}
                    icon="💳"
                    title="Payments: connect Stripe, history and refunds"
                    aria-label="Payments"
                  >
                    <span className="btn-text">Payments</span>
                  </FloatingButton>
                )}
                <FloatingButton
                  variant="secondary"
                  size="medium"
                  onClick={() => setShowAccountSettings(true)}
                  icon="⚙️"
                  title="Account Settings"
                  aria-label="Account settings"
                >
                  <span className="btn-text">Settings</span>
                </FloatingButton>
              </div>
            )}
          </div>
        </div>

        {/* Gaming Profile Content - Floating */}
        <div className="profile-content-gaming-floating">
          {isEditing ? (
            // Edit Form
            <form onSubmit={handleSubmit} className="profile-form-gaming">
              <div className="form-header-gaming">
                <h3>⚡ Update Your Profile</h3>
              </div>

              {/* Read-only identity info */}
              <div className="profile-readonly-row">
                <div className="profile-readonly-item">
                  <span className="profile-readonly-label">📧 Email</span>
                  <span className="profile-readonly-value">{profile.email}</span>
                </div>
                <div className="profile-readonly-item">
                  <span className="profile-readonly-label">🔖 Username</span>
                  <span className="profile-readonly-value">@{profile.username}</span>
                </div>
              </div>

              <div className="profile-form-3col">
                {/* Row 1 */}
                <div className="form-group-gaming">
                  <label>👤 Name</label>
                  <input
                    type="text"
                    name="name"
                    value={profile.name}
                    onChange={handleChange}
                    className="gaming-input"
                    required
                  />
                </div>
                <div className="form-group-gaming">
                  <label>⚧ Gender</label>
                  <select
                    name="gender"
                    value={profile.gender}
                    onChange={handleChange}
                    className="gaming-select"
                  >
                    <option value="">Select gender</option>
                    {GENDER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group-gaming">
                  <label>🎂 Date of Birth</label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={profile.date_of_birth}
                    onChange={handleChange}
                    className="gaming-input"
                    max={new Date().toISOString().slice(0, 10)}
                  />
                  {profile.date_of_birth && (
                    <small className="field-hint">
                      Age: <strong>{calcAge(profile.date_of_birth)}</strong> years old
                    </small>
                  )}
                </div>

                {/* Row 2 */}
                <div className="form-group-gaming">
                  <label>📏 Height (cm)</label>
                  <input
                    type="number"
                    name="height"
                    value={profile.height}
                    onChange={handleChange}
                    className="gaming-input"
                    min="50"
                    max="260"
                    placeholder="Centimeters"
                  />
                </div>
                <div className="form-group-gaming">
                  <label>⚖️ Weight (kg)</label>
                  <input
                    type="number"
                    name="weight"
                    value={profile.weight}
                    onChange={handleChange}
                    className="gaming-input"
                    min="20"
                    max="300"
                    placeholder="Kilograms"
                  />
                </div>
                <div className="form-group-gaming">
                  <label>🔢 Jersey Number</label>
                  <input
                    type="number"
                    name="jersey_number"
                    value={profile.jersey_number}
                    onChange={handleChange}
                    className="gaming-input"
                    min="0"
                    max="999"
                    placeholder="e.g. 23"
                  />
                </div>

                {/* Row 3 */}
                <div className="form-group-gaming">
                  <label>🏷️ Nickname</label>
                  <input
                    type="text"
                    name="nickname"
                    value={profile.nickname}
                    onChange={handleChange}
                    className="gaming-input"
                    placeholder="e.g. The Flash"
                    maxLength={50}
                  />
                </div>

                {/* Favourite Sports — full width */}
                <div className="form-group-gaming form-span-3">
                  <label>⚽ Favourite Sports</label>
                  {selectedSports.length > 0 && (
                    <div className="selected-sports-chips">
                      {selectedSports.map(s => (
                        <span key={s} className="sport-chip sport-chip--selected">
                          {s}
                          <button
                            type="button"
                            className="sport-chip__remove"
                            onClick={() => toggleSport(s)}
                            aria-label={`Remove ${s}`}
                          >✕</button>
                        </span>
                      ))}
                    </div>
                  )}
                  {sportsLoading ? (
                    <p className="sports-loading-hint">Loading sports…</p>
                  ) : (
                    <>
                      <div className="sports-pill-grid">
                        {(showAllSports ? availableSports : availableSports.slice(0, 12)).map(sport => (
                          <button
                            key={sport.id}
                            type="button"
                            className={`sport-pill ${selectedSports.includes(sport.name) ? 'sport-pill--active' : ''}`}
                            onClick={() => toggleSport(sport.name)}
                          >
                            {sport.name}
                          </button>
                        ))}
                      </div>
                      {availableSports.length > 12 && (
                        <button
                          type="button"
                          className="show-more-sports"
                          onClick={() => setShowAllSports(v => !v)}
                        >
                          {showAllSports ? '▲ Show fewer' : `▼ Show all ${availableSports.length} sports`}
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Bio — full width */}
                <div className="form-group-gaming form-span-3">
                  <label>📝 Bio</label>
                  <textarea
                    name="bio"
                    value={profile.bio}
                    onChange={handleChange}
                    className="gaming-textarea"
                    rows="4"
                    placeholder="Tell us about yourself, your achievements, goals..."
                  />
                </div>

                {/* Home City — full width, with Geoapify autocomplete */}
                <div className="form-group-gaming form-span-3">
                  <label>
                    📍 Home City{' '}
                    <span style={{ fontWeight: 400, fontSize: '0.85em', color: 'var(--text-muted, #aaa)' }}>
                      (default location — filters Drop-Ins, Teams, Leagues, Tournaments &amp; Matches)
                    </span>
                  </label>

                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="text"
                        data-testid="city-search-input"
                        className="gaming-input"
                        placeholder="Search your city or district…"
                        value={citySearchInput}
                        onChange={handleCityInput}
                        onFocus={() => setShowCitySuggestions(true)}
                        onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                        autoComplete="off"
                        maxLength={100}
                        style={{ flex: 1 }}
                      />
                      {citySearchLoading && (
                        <span style={{ color: 'var(--text-muted, #aaa)', fontSize: '0.85em' }}>…</span>
                      )}
                      <button
                        type="button"
                        className="gaming-btn gaming-btn--secondary"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={handleCityGps}
                        title="Detect my current city via GPS"
                      >
                        📡 GPS
                      </button>
                      {profile.home_lat != null && (
                        <button
                          type="button"
                          className="gaming-btn gaming-btn--ghost"
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={handleClearCity}
                          title="Remove saved location"
                          aria-label="Remove saved location"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {/* Suggestion dropdown */}
                    {showCitySuggestions && citySuggestions.length > 0 && (
                      <ul
                        data-testid="city-suggestions"
                        style={{
                          position: 'absolute', top: '100%', left: 0, right: 0,
                          zIndex: 100, margin: '0.25rem 0 0',
                          listStyle: 'none', padding: 0,
                          background: 'rgba(10,14,26,0.97)',
                          border: '1px solid rgba(99,102,241,0.4)',
                          borderRadius: '0.4rem',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                          maxHeight: '220px', overflowY: 'auto',
                        }}
                      >
                        {citySuggestions.map((feature, i) => {
                          const p = feature.properties;
                          const label = formatCityLabel(p);
                          return (
                            <li
                              key={p.place_id || i}
                              data-testid={`city-suggestion-${i}`}
                              onMouseDown={() => handleCitySelect(feature)}
                              style={{
                                padding: '0.55rem 0.85rem',
                                cursor: 'pointer',
                                color: '#e2e8f0',
                                fontSize: '0.88rem',
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(99,102,241,0.2)')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              📍 {label}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {profile.home_lat != null && profile.home_lng != null && (
                    <p style={{ fontSize: '0.8em', color: '#22c55e', marginTop: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      ✅ Location saved — nearby content will be filtered to <strong>{profile.home_city}</strong>
                    </p>
                  )}
                </div>
              </div>

              <div className="form-actions-gaming">
                <FloatingButton
                  type="submit"
                  variant="success"
                  size="medium"
                  icon="💾"
                >
                  <span className="btn-text">Save Changes</span>
                </FloatingButton>
                <FloatingButton
                  type="button"
                  variant="outline"
                  size="medium"
                  onClick={handleCancel}
                  icon="❌"
                >
                  <span className="btn-text">Cancel</span>
                </FloatingButton>
              </div>
            </form>
          ) : (
            // Display Mode - Show All Information
            <div className="profile-stats-simple">
              <h3 className="stats-title">🎮 Player Info</h3>
              
              <div className="stats-direct-grid">
                {/* Basic Information */}
                <div className="stat-item-direct">
                  <span className="stat-label-direct">👤 Name</span>
                  <span className="stat-value-direct">{profile.name || "Not set"}</span>
                </div>
                
                <div className="stat-item-direct">
                  <span className="stat-label-direct">📧 Email</span>
                  <span className="stat-value-direct">{profile.email || "Not set"}</span>
                </div>
                
                <div className="stat-item-direct">
                  <span className="stat-label-direct">🔖 Username</span>
                  <span className="stat-value-direct">{profile.username ? `@${profile.username}` : "Not set"}</span>
                </div>

                <div className="stat-item-direct">
                  <span className="stat-label-direct">⚧ Gender</span>
                  <span className="stat-value-direct">
                    {GENDER_OPTIONS.find(o => o.value === profile.gender)?.label || "Not set"}
                  </span>
                </div>
                
                {/* Physical Stats */}
                <div className="stat-item-direct">
                  <span className="stat-label-direct">🎂 Date of Birth</span>
                  <span className="stat-value-direct">
                    {profile.date_of_birth
                      ? `${new Date(profile.date_of_birth).toLocaleDateString()} (${calcAge(profile.date_of_birth)} yrs)`
                      : "Not set"}
                  </span>
                </div>
                
                <div className="stat-item-direct">
                  <span className="stat-label-direct">📏 Height</span>
                  <span className="stat-value-direct">{profile.height ? `${profile.height} cm` : "Not set"}</span>
                </div>
                
                <div className="stat-item-direct">
                  <span className="stat-label-direct">⚖️ Weight</span>
                  <span className="stat-value-direct">{profile.weight ? `${profile.weight} kg` : "Not set"}</span>
                </div>

                <div className="stat-item-direct stat-item-direct--full">
                  <span className="stat-label-direct">📍 Home City</span>
                  {profile.home_city ? (
                    <span className="stat-value-direct" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {profile.home_city}
                      {profile.home_lat != null && cityFromGps && (
                        <span style={{ fontSize: '0.78em', color: 'var(--text-muted, #94a3b8)', fontWeight: 400 }}>
                          (📡 GPS verified)
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="stat-value-direct" style={{ color: 'var(--text-muted, #94a3b8)' }}>
                      Not set —{' '}
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        style={{ background: 'none', border: 'none', color: 'var(--accent, #6366f1)', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' }}
                      >
                        Add your city
                      </button>
                      {' '}to filter nearby content
                    </span>
                  )}
                </div>

                {/* Sports Information */}
                <div className="stat-item-direct stat-item-direct--full">
                  <span className="stat-label-direct">⚽ Favourite Sports</span>
                  {selectedSports.length > 0 ? (
                    <div className="display-sports-chips">
                      {selectedSports.map(s => (
                        <span key={s} className="sport-chip sport-chip--display">{s}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="stat-value-direct">Not set</span>
                  )}
                </div>
              </div>
              
              <div className="bio-section-direct">
                <h4 className="bio-title">📝 Bio</h4>
                <p className="bio-text">{profile.bio || "No bio added yet."}</p>
              </div>
            </div>
          )}

          {message && (
            <div
              className={`gaming-message-floating ${message.includes("success") ? "success" : "error"}`}
            >
              <span className="message-icon">
                {message.includes("success") ? "✅" : "❌"}
              </span>
              <span className="message-text">{message}</span>
            </div>
          )}
        </div>

        {loading && (
          <div className="loading-content-gaming-floating">
            <div className="loading-spinner-gaming"></div>
            <p>Loading player profile...</p>
          </div>
        )}

        {showUpgradeModal && (
          <div className="upgrade-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="host-league-title">
            <div className="upgrade-modal">
              <div className="upgrade-modal-header">
                <div>
                  <h3 id="host-league-title">
                    Host a league <span className="beta-badge">Beta</span>
                  </h3>
                  <p>Run leagues and tournaments on Sportifide: schedules, standings, results and announcements.</p>
                </div>
                <button
                  type="button"
                  className="upgrade-modal-close"
                  onClick={() => setShowUpgradeModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="upgrade-plans">
                <div className="upgrade-plan-card upgrade-plan-card--static selected">
                  <div className="plan-title">League host</div>
                  <div className="plan-price">Free during beta</div>
                  <div className="plan-target">
                    Our team reviews each request, usually within 24 to 48 hours. You'll get an email and a notification.
                  </div>
                </div>
              </div>

              <p className="upgrade-note">
                Sportifide is in beta, so hosting is free while we gather feedback from organizers.
                We'll tell you well ahead of any paid plan.
              </p>

              <p className="upgrade-note">
                Drop-ins and friendly matches are already free for every player.
                Managing many players or students for a school or club?{" "}
                <a href="mailto:hello@sportifide.com">Contact us at hello@sportifide.com</a>.
              </p>

              {upgradeStatus && (
                <div className="upgrade-status" role="status">{upgradeStatus}</div>
              )}
              {upgradeError && <div className="upgrade-error" role="alert">{upgradeError}</div>}

              <div className="upgrade-modal-actions">
                <button
                  type="button"
                  className="upgrade-cancel"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  {upgradeStatus ? "Close" : "Cancel"}
                </button>
                {!upgradeStatus && (
                  <button
                    type="button"
                    className="upgrade-submit"
                    onClick={handleUpgrade}
                    disabled={upgradeLoading}
                  >
                    {upgradeLoading ? "Sending..." : "Request to host"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {showAvatarModal && (
          <div className="avatar-modal-overlay" role="dialog" aria-modal="true">
            <div className="avatar-modal">
              <div className="avatar-modal-header">
                <div>
                  <h3>Choose Your Avatar</h3>
                  <p>Select your favorite sports athlete</p>
                </div>
                <button
                  type="button"
                  className="avatar-modal-close"
                  onClick={() => setShowAvatarModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {avatarError && <div className="avatar-error">{avatarError}</div>}

              <div className="avatar-grid">
                {SPORTS_AVATARS.map(avatar => (
                  <div
                    key={avatar.id}
                    className="avatar-card"
                    onClick={() => handleAvatarSelect(avatar.url, avatar.type)}
                    role="button"
                    tabIndex="0"
                    title={avatar.name}
                  >
                    <img src={avatar.url} alt={avatar.name} className="avatar-preview" />
                    <p className="avatar-name">{avatar.emoji} {avatar.name}</p>
                  </div>
                ))}
              </div>

              {/* Custom Avatar Upload Section */}
              <div className="avatar-upload-section">
                <h4>📤 Or Upload Your Own</h4>
                <p className="upload-hint">Upload a custom image (PNG, JPG, max 5MB)</p>
                
                {customFileError && <div className="avatar-error">{customFileError}</div>}

                <div className="avatar-file-input-wrapper">
                  <input
                    type="file"
                    id="avatar-file-input"
                    accept="image/*"
                    onChange={handleCustomFileSelect}
                    disabled={customFileUploading || compressingImage}
                    className="avatar-file-input"
                  />
                  <label htmlFor="avatar-file-input" className="avatar-file-label">
                    <span className="file-icon">📁</span>
                    <span className="file-text">
                      {compressingImage ? 'Processing Image...' : customFilePreview ? 'Change Image' : 'Choose Image'}
                    </span>
                  </label>
                </div>

                {compressingImage && (
                  <div className="avatar-compressing">
                    <span className="spinner-mini">🔄</span>
                    <span>Compressing image...</span>
                  </div>
                )}

                {customFilePreview && (
                  <div className="avatar-preview-section">
                    <div className="custom-avatar-preview">
                      <img src={customFilePreview} alt="Preview" />
                    </div>
                    <button
                      type="button"
                      className="avatar-upload-btn"
                      onClick={handleUploadCustomAvatar}
                      disabled={customFileUploading}
                    >
                      {customFileUploading ? '⏳ Uploading...' : '✅ Use This Avatar'}
                    </button>
                  </div>
                )}
              </div>

              <div className="avatar-modal-actions">
                <button
                  type="button"
                  className="avatar-cancel"
                  onClick={() => setShowAvatarModal(false)}
                  disabled={avatarLoading || customFileUploading}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Account Settings Modal ─────────────────────────────── */}
        {showAccountSettings && (
          <div className="upgrade-modal-overlay" role="dialog" aria-modal="true" onClick={closeAccountSettings}>
            <div className="upgrade-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>

              {/* ── Menu ── */}
              {accountView === "menu" && (
                <>
                  <div className="upgrade-modal-header">
                    <div>
                      <h3>⚙️ Account Settings</h3>
                      <p>Manage your account security and status.</p>
                    </div>
                    <button className="upgrade-modal-close" onClick={closeAccountSettings} aria-label="Close">×</button>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "1rem" }}>
                    <button
                      className="upgrade-submit"
                      style={{ background: "linear-gradient(135deg,#4f8ef7,#2563eb)", marginBottom: 0 }}
                      onClick={() => { setAccountActionMsg(""); setAccountView("change-password"); }}
                    >
                      🔑 Change Password
                    </button>
                    <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", margin: "-4px 0 6px 2px" }}>
                      Update your login password from here.
                    </p>
                    <button
                      className="upgrade-submit"
                      style={{ background: "linear-gradient(135deg,#6b7280,#374151)", marginBottom: 0 }}
                      onClick={() => { setAccountActionMsg(""); setAccountView("blocked"); }}
                    >
                      🚫 Blocked and muted players
                    </button>
                    <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", margin: "-4px 0 6px 2px" }}>
                      See who you blocked or muted, and undo it.
                    </p>
                    <button
                      className="upgrade-submit"
                      style={{ background: "linear-gradient(135deg,#fd7e14,#e05c00)", marginBottom: 0 }}
                      onClick={() => { setAccountActionMsg(""); setAccountView("deactivate-confirm"); }}
                    >
                      ⏸ Deactivate Account
                    </button>
                    <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", margin: "-4px 0 6px 2px" }}>
                      Temporarily disables your account.{" "}
                      <a href="/reactivate-account" style={{ color: "#fd7e14", textDecoration: "underline" }}>Reactivate anytime here.</a>
                    </p>
                    <button
                      className="upgrade-submit"
                      style={{ background: "linear-gradient(135deg,#dc3545,#a71d2a)", marginBottom: 0 }}
                      onClick={() => { setAccountActionMsg(""); setAccountView("delete-confirm"); }}
                    >
                      🗑 Permanently Delete Account
                    </button>
                    <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", margin: "-4px 0 0 2px" }}>
                      Irreversible. All your data will be removed.
                    </p>
                  </div>

                  <div className="upgrade-modal-actions" style={{ marginTop: "1.5rem" }}>
                    <button className="upgrade-cancel" onClick={closeAccountSettings}>Close</button>
                  </div>
                </>
              )}

              {/* ── Blocked and muted players ── */}
              {accountView === "blocked" && (
                <>
                  <div className="upgrade-modal-header">
                    <div>
                      <h3>🚫 Blocked and muted</h3>
                      <p>Blocked players can't message, follow or invite you. You get no alerts from muted players.</p>
                    </div>
                    <button className="upgrade-modal-close" onClick={closeAccountSettings} aria-label="Close">×</button>
                  </div>
                  <BlockedUsersList />
                  <div className="upgrade-modal-actions" style={{ marginTop: "1.5rem" }}>
                    <button className="upgrade-cancel" onClick={() => setAccountView("menu")}>Back</button>
                  </div>
                </>
              )}

              {/* ── Change Password ── */}
              {accountView === "change-password" && (
                <>
                  <div className="upgrade-modal-header">
                    <div>
                      <h3>🔑 Change Password</h3>
                      <p>Enter your current password and choose a new one.</p>
                    </div>
                    <button className="upgrade-modal-close" onClick={closeAccountSettings} aria-label="Close">×</button>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "1rem" }}>
                    {/* Current password */}
                    <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>Current Password *</label>
                    <div className="password-wrapper">
                      <input
                        type={cpShowCurrent ? "text" : "password"}
                        value={cpCurrentPwd}
                        onChange={e => { setCpCurrentPwd(e.target.value); setAccountActionMsg(""); }}
                        placeholder="Your current password"
                        autoComplete="current-password"
                        style={{ background: "rgba(255,255,255,0.07)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "9px 40px 9px 12px", width: "100%", boxSizing: "border-box" }}
                      />
                      <button type="button" className="eye-toggle" onClick={() => setCpShowCurrent(v => !v)} aria-label="Toggle visibility">
                        {cpShowCurrent ? "🙈" : "👁️"}
                      </button>
                    </div>

                    {/* New password */}
                    <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>New Password *</label>
                    <div className="password-wrapper">
                      <input
                        type={cpShowNew ? "text" : "password"}
                        value={cpNewPwd}
                        onChange={e => { setCpNewPwd(e.target.value); setAccountActionMsg(""); }}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        style={{ background: "rgba(255,255,255,0.07)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "9px 40px 9px 12px", width: "100%", boxSizing: "border-box" }}
                      />
                      <button type="button" className="eye-toggle" onClick={() => setCpShowNew(v => !v)} aria-label="Toggle visibility">
                        {cpShowNew ? "🙈" : "👁️"}
                      </button>
                    </div>

                    {/* Confirm new password */}
                    <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>Confirm New Password *</label>
                    <div className="password-wrapper">
                      <input
                        type={cpShowConfirm ? "text" : "password"}
                        value={cpConfirmPwd}
                        onChange={e => { setCpConfirmPwd(e.target.value); setAccountActionMsg(""); }}
                        placeholder="Repeat new password"
                        autoComplete="new-password"
                        style={{ background: "rgba(255,255,255,0.07)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "9px 40px 9px 12px", width: "100%", boxSizing: "border-box" }}
                      />
                      <button type="button" className="eye-toggle" onClick={() => setCpShowConfirm(v => !v)} aria-label="Toggle visibility">
                        {cpShowConfirm ? "🙈" : "👁️"}
                      </button>
                    </div>

                    {accountActionMsg && (
                      <p style={{ color: accountActionMsg.startsWith("✅") ? "#4ade80" : "#ff6b6b", fontSize: "0.85rem", margin: 0 }}>
                        {accountActionMsg}
                      </p>
                    )}
                  </div>

                  <div className="upgrade-modal-actions">
                    <button className="upgrade-cancel" onClick={() => { setAccountView("menu"); setAccountActionMsg(""); }}>Back</button>
                    <button
                      className="upgrade-submit"
                      disabled={accountActionLoading || !cpCurrentPwd || !cpNewPwd || !cpConfirmPwd}
                      onClick={async () => {
                        setAccountActionMsg("");
                        if (cpNewPwd !== cpConfirmPwd) { setAccountActionMsg("New passwords do not match."); return; }
                        const passwordError = passwordProblem(cpNewPwd);
                        if (passwordError) { setAccountActionMsg(passwordError); return; }
                        setAccountActionLoading(true);
                        try {
                          await changePassword(cpCurrentPwd, cpNewPwd, cpConfirmPwd);
                          // The server ends every session signed in with the old password
                          setAccountActionMsg("✅ Password changed. Please sign in again with your new password.");
                          setCpCurrentPwd(""); setCpNewPwd(""); setCpConfirmPwd("");
                          setTimeout(() => {
                            localStorage.clear();
                            window.location.href = "/login";
                          }, 2000);
                        } catch (e) {
                          setAccountActionMsg(e.response?.data?.message || e.response?.data?.error || "Failed. Please try again.");
                        } finally {
                          setAccountActionLoading(false);
                        }
                      }}
                    >
                      {accountActionLoading ? "Updating…" : "Update Password"}
                    </button>
                  </div>
                </>
              )}

              {/* ── Deactivate Confirm ── */}
              {accountView === "deactivate-confirm" && (
                <>
                  <div className="upgrade-modal-header">
                    <div>
                      <h3 style={{ color: "#fd7e14" }}>⏸ Deactivate Account?</h3>
                      <p>Are you sure you want to deactivate your account?</p>
                    </div>
                    <button className="upgrade-modal-close" onClick={closeAccountSettings} aria-label="Close">×</button>
                  </div>

                  <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem", lineHeight: 1.6, margin: "0.75rem 0 1rem" }}>
                    Your account will be <strong style={{ color: "#fd7e14" }}>temporarily disabled</strong>. You can reactivate it anytime using our{" "}
                    <a href="/reactivate-account" style={{ color: "#fd7e14", textDecoration: "underline" }}>self-service reactivation page</a>{" "}
                    or by emailing <a href="mailto:hello@sportifide.com" style={{ color: "#fd7e14" }}>hello@sportifide.com</a>.
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "1rem" }}>
                    <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>Reason (optional)</label>
                    <input
                      type="text"
                      value={accountReason}
                      onChange={e => setAccountReason(e.target.value)}
                      placeholder="Taking a break…"
                      style={{ background: "rgba(255,255,255,0.07)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "9px 12px" }}
                    />
                  </div>

                  {accountActionMsg && <p style={{ color: "#ff6b6b", fontSize: "0.85rem", margin: "0 0 8px" }}>{accountActionMsg}</p>}

                  <div className="upgrade-modal-actions">
                    <button className="upgrade-cancel" onClick={() => { setAccountView("menu"); setAccountActionMsg(""); }}>Cancel</button>
                    <button
                      className="upgrade-submit"
                      style={{ background: "linear-gradient(135deg,#fd7e14,#e05c00)" }}
                      disabled={accountActionLoading}
                      onClick={async () => {
                        setAccountActionLoading(true);
                        setAccountActionMsg("");
                        try {
                          await deactivateAccount(accountReason);
                          localStorage.clear();
                          window.location.href = "/login";
                        } catch (e) {
                          setAccountActionMsg(e.response?.data?.message || "Failed. Please try again.");
                          setAccountActionLoading(false);
                        }
                      }}
                    >
                      {accountActionLoading ? "Processing…" : "Yes, Deactivate"}
                    </button>
                  </div>
                </>
              )}

              {/* ── Delete Confirm ── */}
              {accountView === "delete-confirm" && (
                <>
                  <div className="upgrade-modal-header">
                    <div>
                      <h3 style={{ color: "#dc3545" }}>🗑 Delete Account?</h3>
                      <p>This action is permanent and cannot be undone.</p>
                    </div>
                    <button className="upgrade-modal-close" onClick={closeAccountSettings} aria-label="Close">×</button>
                  </div>

                  <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem", lineHeight: 1.6, margin: "0.75rem 0 1rem" }}>
                    Are you sure you want to <strong style={{ color: "#dc3545" }}>permanently delete</strong> your account? All your data, sports, teams and history will be removed forever.
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "1rem" }}>
                    <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>Confirm Password *</label>
                    <input
                      type="password"
                      value={accountPassword}
                      onChange={e => { setAccountPassword(e.target.value); setAccountActionMsg(""); }}
                      placeholder="Your current password"
                      autoComplete="current-password"
                      style={{ background: "rgba(255,255,255,0.07)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "9px 12px" }}
                    />
                    <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>Reason (optional)</label>
                    <input
                      type="text"
                      value={accountReason}
                      onChange={e => setAccountReason(e.target.value)}
                      placeholder="Reason for leaving…"
                      style={{ background: "rgba(255,255,255,0.07)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "9px 12px" }}
                    />
                  </div>

                  {accountActionMsg && <p style={{ color: "#ff6b6b", fontSize: "0.85rem", margin: "0 0 8px" }}>{accountActionMsg}</p>}

                  <div className="upgrade-modal-actions">
                    <button className="upgrade-cancel" onClick={() => { setAccountView("menu"); setAccountActionMsg(""); setAccountPassword(""); }}>Cancel</button>
                    <button
                      className="upgrade-submit"
                      style={{ background: "linear-gradient(135deg,#dc3545,#a71d2a)" }}
                      disabled={accountActionLoading || !accountPassword}
                      onClick={async () => {
                        setAccountActionLoading(true);
                        setAccountActionMsg("");
                        try {
                          await deleteAccount(accountPassword, accountReason);
                          localStorage.clear();
                          window.location.href = "/login";
                        } catch (e) {
                          setAccountActionMsg(e.response?.data?.message || "Failed. Please try again.");
                          setAccountActionLoading(false);
                        }
                      }}
                    >
                      {accountActionLoading ? "Deleting…" : "Yes, Delete My Account"}
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        )}

        <NotificationPreferencesModal
          isOpen={showNotificationSettings}
          onClose={() => setShowNotificationSettings(false)}
        />
      </div>
    </GameLayout>
  );
}

export default Profile;
